/**
 * `redirect_url` is attacker controllable: anybody can craft a link to this
 * page. Navigating to it unvalidated turned the page into a javascript:/data:
 * execution sink and an unrestricted open redirect, so only http(s) targets are
 * accepted, and plain http only on a local host.
 *
 * A host allowlist is the stronger control. When PAG_REDIRECT_HOSTS is set at
 * build time (comma separated, e.g. "app.pagfinance.com,pagcrypto.finance"),
 * only those hosts and their subdomains are accepted.
 */
const SAFE_PROTOCOLS = ["https:", "http:"];

const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1", "[::1]", "::1"];

const allowedHosts = (process.env.REACT_APP_REDIRECT_HOSTS || "")
  .split(",")
  .map((host) => host.trim().toLowerCase())
  .filter(Boolean);

const isLocal = (hostname) => LOCAL_HOSTNAMES.includes(hostname.toLowerCase());

function isAllowedHost(hostname) {
  if (allowedHosts.length === 0) return true;
  const host = hostname.toLowerCase();
  return allowedHosts.some(
    (allowed) => host === allowed || host.endsWith(`.${allowed}`)
  );
}

/**
 * Callers should percent-encode `redirect_url`, but plenty pass it raw. The raw
 * value is tried first so an unencoded URL keeps its own query string, then the
 * decoded form for callers that did the right thing.
 */
function toUrl(rawValue) {
  const candidates = [rawValue];

  try {
    const decoded = decodeURIComponent(rawValue);
    if (decoded !== rawValue) candidates.push(decoded);
  } catch (err) {
    // Malformed percent-encoding: the raw value is all we have.
  }

  for (const candidate of candidates) {
    try {
      return new URL(candidate);
    } catch (err) {
      // Not a URL in this form, try the next candidate.
    }
  }

  return null;
}

/**
 * Builds the destination for a scanned code, or null when `redirect_url` is
 * absent or not a safe target.
 */
export function buildRedirectTarget(rawRedirectUrl, barcode) {
  if (!rawRedirectUrl || !barcode) return null;

  const url = toUrl(rawRedirectUrl);
  if (!url) return null;
  if (!SAFE_PROTOCOLS.includes(url.protocol)) return null;
  if (url.protocol === "http:" && !isLocal(url.hostname)) return null;
  if (!isAllowedHost(url.hostname)) return null;

  url.searchParams.set("barcode", barcode);
  return url.toString();
}

/** True when `redirect_url` was supplied but rejected, so the UI can say so. */
export function isRejectedRedirect(rawRedirectUrl) {
  if (!rawRedirectUrl) return false;
  return buildRedirectTarget(rawRedirectUrl, "probe") === null;
}
